"use client"

import { useState, useEffect } from "react"
import { useSearchParams, useRouter } from "next/navigation"
import type { Product } from "@/lib/types"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { ProductGrid } from "@/components/product-grid"

import { ManufacturerFilter } from "@/components/manufacturer-filter"
import { Search, X } from "lucide-react"

interface ProductsContentProps {
  initialProducts: Product[]
  totalProducts?: number
  currentPage?: number
}

export function ProductsContent({ initialProducts, totalProducts = 0, currentPage = 1 }: ProductsContentProps) {
  const searchParams = useSearchParams()
  const categoryParam = searchParams.get("category")
  const searchParam = searchParams.get("search")

  // We rely on server for main filtering now, local search is still useful for small sets 
  // but might be confusing if pagination exists. Ideally search should also be server side.
  // For now let's keep local filtering on the CURRENT PAGE data only, which is the standard behavior for mixed modes.

  const [filteredProducts, setFilteredProducts] = useState<Product[]>(initialProducts)
  const [searchQuery, setSearchQuery] = useState(searchParam || "")
  const [selectedCategory, setSelectedCategory] = useState<string | null>(categoryParam)
  const [sortBy, setSortBy] = useState<string>("date")

  const getRating = (product: Product) => {
    return 3.5 + ((product.id * 17) % 15) / 10
  }

  useEffect(() => {
    // Update local state when server data changes (e.g. page change)
    setFilteredProducts(initialProducts)
  }, [initialProducts])

  // Local filtering and sorting effect
  useEffect(() => {
    let filtered = [...initialProducts]
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(
        (p) =>
          p.name.toLowerCase().includes(query) ||
          (p.description && p.description.toLowerCase().includes(query)) ||
          (p.product_type && p.product_type.toLowerCase().includes(query)) ||
          (p.company_name && p.company_name.toLowerCase().includes(query)) ||
          (p.sku && p.sku.toLowerCase().includes(query)),
      )
    }

    // Apply Sorting
    filtered.sort((a, b) => {
      if (sortBy === "name") {
        return a.name.localeCompare(b.name)
      }
      if (sortBy === "price-low") {
        return a.price - b.price
      }
      if (sortBy === "price-high") {
        return b.price - a.price
      }
      if (sortBy === "rating") {
        return getRating(b) - getRating(a)
      }
      if (sortBy === "availability") {
        return (b.stock_quantity > 0 ? 1 : 0) - (a.stock_quantity > 0 ? 1 : 0)
      }
      // Default: date (newest first)
      const dateA = a.created_at ? new Date(a.created_at).getTime() : a.id
      const dateB = b.created_at ? new Date(b.created_at).getTime() : b.id
      return dateB - dateA
    })

    setFilteredProducts(filtered)
  }, [searchQuery, sortBy, initialProducts])

  // Sync state with URL params
  useEffect(() => {
    setSelectedCategory(categoryParam)
    if (searchParam !== null) {
      setSearchQuery(searchParam)
    }
  }, [categoryParam, searchParam])

  const router = useRouter()

  const handleClearFilters = () => {
    setSearchQuery("")
    setSelectedCategory(null)
    setSortBy("date")
    router.push("/products")
  }

  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams.toString())
    params.set("page", newPage.toString())
    router.push(`/products?${params.toString()}`)
  }

  const pageSize = 48
  const startItem = (currentPage - 1) * pageSize + 1
  const endItem = Math.min(currentPage * pageSize, totalProducts)
  const totalPages = Math.ceil(totalProducts / pageSize)

  return (
    <main className="min-h-screen bg-background">
      <div className="mx-auto max-w-[98%] px-4 py-8 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold tracking-tight sm:text-4xl mb-2">
          {selectedCategory && selectedCategory !== "all"
            ? selectedCategory
              .split("-")
              .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
              .join(" ")
            : "Product Catalog"}
        </h1>

        <div className="mb-8 space-y-4">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search Bar */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search products by name, type, or description..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Sort Dropdown */}
            <div className="flex items-center gap-2">
              <label htmlFor="sort-select" className="text-sm font-medium text-muted-foreground whitespace-nowrap">Sort by:</label>
              <select
                id="sort-select"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 cursor-pointer"
              >
                <option value="date">Date</option>
                <option value="name">Name</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
                <option value="rating">Rating</option>
                <option value="availability">Availability</option>
              </select>
            </div>
          </div>

          {/* Active Filters */}
          {searchQuery && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-muted-foreground mr-1">Active filters:</span>
              <Badge variant="secondary" className="gap-1 pr-1">
                Search: {searchQuery}
                <button
                  onClick={() => setSearchQuery("")}
                  className="ml-1 rounded-full hover:bg-muted p-0.5"
                >
                  <X className="h-3 w-3" />
                  <span className="sr-only">Remove search filter</span>
                </button>
              </Badge>
              <Button variant="ghost" size="sm" onClick={handleClearFilters} className="ml-auto text-muted-foreground h-7">
                Clear all
              </Button>
            </div>
          )}

          <div className="flex items-center justify-between mt-2">
            <div className="text-sm text-muted-foreground">
              {totalProducts > 0
                ? `Showing ${startItem}-${endItem} of ${totalProducts} results`
                : "No products found"}
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage <= 1}
                >
                  Previous
                </Button>
                <div className="flex items-center px-2 text-sm font-medium">
                  Page {currentPage} of {totalPages}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage >= totalPages}
                >
                  Next
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Products Grid */}
        {filteredProducts.length > 0 ? (
          <>
            <ProductGrid products={filteredProducts} />

            {/* Bottom Pagination */}
            <div className="flex items-center justify-between mt-8 border-t pt-4">
              <div className="text-sm text-muted-foreground">
                {totalProducts > 0
                  ? `Showing ${startItem}-${endItem} of ${totalProducts} results`
                  : ""}
              </div>

              {totalPages > 1 && (
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage <= 1}
                  >
                    Previous
                  </Button>
                  <div className="flex items-center px-2 text-sm font-medium">
                    Page {currentPage} of {totalPages}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage >= totalPages}
                  >
                    Next
                  </Button>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-20">
            <p className="text-lg text-muted-foreground">No products found</p>
            <Button variant="link" onClick={handleClearFilters} className="mt-4">
              Clear filters and try again
            </Button>
          </div>
        )}
      </div>
    </main>
  )
}
